import { useCallback, useState } from "react";

// Figma/Illustrator-style alignment snapping, shared by the redaction editor
// and the form designer/filler. A dragged rect's edges and centers snap to
// the edges/centers of the other rects on the page (and to the page's center
// lines) when within a small screen-pixel threshold; the matched lines are
// exposed as guides so they can be drawn during the drag.
// All coordinates are unscaled PDF points.

interface Rect {
  x: number;
  y: number;
  width: number;
  height: number;
}

export interface SnapGuide {
  orientation: "vertical" | "horizontal";
  position: number; // x for vertical guides, y for horizontal
}

interface SnapLines {
  xLines: number[];
  yLines: number[];
}

// Candidate alignment lines: left/center/right and top/center/bottom of every
// other rect, plus the page's center lines.
function collectSnapLines(
  rects: Rect[],
  pageWidth: number,
  pageHeight: number
): SnapLines {
  const xLines: number[] = [pageWidth / 2];
  const yLines: number[] = [pageHeight / 2];
  for (const r of rects) {
    xLines.push(r.x, r.x + r.width / 2, r.x + r.width);
    yLines.push(r.y, r.y + r.height / 2, r.y + r.height);
  }
  return { xLines, yLines };
}

// Snap a rect against the candidate lines. Each axis snaps independently to
// its nearest line within the threshold.
function snapRectToLines(
  rect: Rect,
  lines: SnapLines,
  threshold: number
): { rect: Rect; guides: SnapGuide[] } {
  const xAnchors = [rect.x, rect.x + rect.width / 2, rect.x + rect.width];
  const yAnchors = [rect.y, rect.y + rect.height / 2, rect.y + rect.height];

  let bestDx: number | null = null;
  let bestXLine: number | null = null;
  for (const line of lines.xLines) {
    for (const anchor of xAnchors) {
      const d = line - anchor;
      if (
        Math.abs(d) <= threshold &&
        (bestDx === null || Math.abs(d) < Math.abs(bestDx))
      ) {
        bestDx = d;
        bestXLine = line;
      }
    }
  }

  let bestDy: number | null = null;
  let bestYLine: number | null = null;
  for (const line of lines.yLines) {
    for (const anchor of yAnchors) {
      const d = line - anchor;
      if (
        Math.abs(d) <= threshold &&
        (bestDy === null || Math.abs(d) < Math.abs(bestDy))
      ) {
        bestDy = d;
        bestYLine = line;
      }
    }
  }

  const guides: SnapGuide[] = [];
  if (bestXLine !== null) {
    guides.push({ orientation: "vertical", position: bestXLine });
  }
  if (bestYLine !== null) {
    guides.push({ orientation: "horizontal", position: bestYLine });
  }

  return {
    rect: {
      ...rect,
      x: rect.x + (bestDx ?? 0),
      y: rect.y + (bestDy ?? 0),
    },
    guides,
  };
}

interface UseSnapGuidesOptions {
  /** All rects on the current page (the dragged one is excluded per call). */
  getTargetRects: () => { id: string; rect: Rect }[];
  /** Extra ids to exclude from targets, e.g. the current multi-selection. */
  getExcludedIds?: () => Set<string>;
  pageWidth: number;
  pageHeight: number;
  scale: number;
  /** Snap distance in screen pixels (zoom-independent). Default 6. */
  thresholdPx?: number;
  /** localStorage key persisting the enabled toggle. */
  storageKey?: string;
}

interface UseSnapGuidesResult {
  snapEnabled: boolean;
  setSnapEnabled: (enabled: boolean) => void;
  /** Guide lines to draw (empty when not dragging or nothing snapped). */
  guides: SnapGuide[];
  clearGuides: () => void;
  /** Snap a rect for commit. No guide updates. Pass to useFieldMovement.snapRect. */
  snapRect: (fieldId: string, rect: Rect) => Rect;
  /** Update guides during a live drag without moving the rect (react-rnd owns
   *  the position mid-drag). Pass to useFieldMovement.onLiveDrag. */
  previewSnap: (fieldId: string, rect: Rect) => void;
  /** Snap a rect AND update guides — for custom (non-Rnd) live drags. */
  liveSnap: (fieldId: string, rect: Rect) => Rect;
  /** Snap a resize for commit: only the dragged edge(s) snap, so the box
   *  grows/shrinks to the line. Pass to useFieldMovement.snapResizeRect. */
  snapResizeRect: (fieldId: string, rect: Rect, dir: string) => Rect;
  /** Update guides during a live resize. Pass to useFieldMovement.onLiveResize. */
  previewResizeSnap: (fieldId: string, rect: Rect, dir: string) => void;
}

// Nearest line within threshold, or null.
function nearestLine(
  lines: number[],
  value: number,
  threshold: number
): number | null {
  let best: number | null = null;
  for (const line of lines) {
    if (
      Math.abs(line - value) <= threshold &&
      (best === null || Math.abs(line - value) < Math.abs(best - value))
    ) {
      best = line;
    }
  }
  return best;
}

export function useSnapGuides({
  getTargetRects,
  getExcludedIds,
  pageWidth,
  pageHeight,
  scale,
  thresholdPx = 6,
  storageKey = "pnai-snapGuidesEnabled",
}: UseSnapGuidesOptions): UseSnapGuidesResult {
  const [snapEnabled, setSnapEnabledState] = useState(
    () => localStorage.getItem(storageKey) !== "false"
  );
  const setSnapEnabled = useCallback(
    (enabled: boolean) => {
      setSnapEnabledState(enabled);
      localStorage.setItem(storageKey, String(enabled));
    },
    [storageKey]
  );

  const [guides, setGuides] = useState<SnapGuide[]>([]);
  const clearGuides = useCallback(() => {
    setGuides((prev) => (prev.length === 0 ? prev : []));
  }, []);

  const evaluate = useCallback(
    (fieldId: string, rect: Rect) => {
      if (!snapEnabled) return { rect, guides: [] as SnapGuide[] };
      const excluded = new Set(getExcludedIds?.() ?? []);
      excluded.add(fieldId);
      const lines = collectSnapLines(
        getTargetRects()
          .filter((t) => !excluded.has(t.id))
          .map((t) => t.rect),
        pageWidth,
        pageHeight
      );
      return snapRectToLines(rect, lines, thresholdPx / scale);
    },
    [snapEnabled, getTargetRects, getExcludedIds, pageWidth, pageHeight, thresholdPx, scale]
  );

  const snapRect = useCallback(
    (fieldId: string, rect: Rect) => evaluate(fieldId, rect).rect,
    [evaluate]
  );

  const previewSnap = useCallback(
    (fieldId: string, rect: Rect) => {
      setGuides(evaluate(fieldId, rect).guides);
    },
    [evaluate]
  );

  const liveSnap = useCallback(
    (fieldId: string, rect: Rect) => {
      const result = evaluate(fieldId, rect);
      setGuides(result.guides);
      return result.rect;
    },
    [evaluate]
  );

  // Resize: snap only the edge(s) being dragged (react-rnd direction string,
  // e.g. "right", "bottomLeft") so the box stretches to the line instead of
  // shifting position.
  const evaluateResize = useCallback(
    (fieldId: string, rect: Rect, dir: string) => {
      if (!snapEnabled) return { rect, guides: [] as SnapGuide[] };
      const excluded = new Set(getExcludedIds?.() ?? []);
      excluded.add(fieldId);
      const lines = collectSnapLines(
        getTargetRects()
          .filter((t) => !excluded.has(t.id))
          .map((t) => t.rect),
        pageWidth,
        pageHeight
      );
      const threshold = thresholdPx / scale;
      const d = dir.toLowerCase();
      const guides: SnapGuide[] = [];
      let { x, y, width, height } = rect;

      if (d.includes("left")) {
        const line = nearestLine(lines.xLines, x, threshold);
        if (line !== null) {
          width += x - line;
          x = line;
          guides.push({ orientation: "vertical", position: line });
        }
      } else if (d.includes("right")) {
        const line = nearestLine(lines.xLines, x + width, threshold);
        if (line !== null) {
          width = line - x;
          guides.push({ orientation: "vertical", position: line });
        }
      }

      if (d.includes("top")) {
        const line = nearestLine(lines.yLines, y, threshold);
        if (line !== null) {
          height += y - line;
          y = line;
          guides.push({ orientation: "horizontal", position: line });
        }
      } else if (d.includes("bottom")) {
        const line = nearestLine(lines.yLines, y + height, threshold);
        if (line !== null) {
          height = line - y;
          guides.push({ orientation: "horizontal", position: line });
        }
      }

      if (width < 1 || height < 1) {
        return { rect, guides: [] as SnapGuide[] };
      }
      return { rect: { x, y, width, height }, guides };
    },
    [snapEnabled, getTargetRects, getExcludedIds, pageWidth, pageHeight, thresholdPx, scale]
  );

  const snapResizeRect = useCallback(
    (fieldId: string, rect: Rect, dir: string) =>
      evaluateResize(fieldId, rect, dir).rect,
    [evaluateResize]
  );

  const previewResizeSnap = useCallback(
    (fieldId: string, rect: Rect, dir: string) => {
      setGuides(evaluateResize(fieldId, rect, dir).guides);
    },
    [evaluateResize]
  );

  return {
    snapEnabled,
    setSnapEnabled,
    guides,
    clearGuides,
    snapRect,
    previewSnap,
    liveSnap,
    snapResizeRect,
    previewResizeSnap,
  };
}
